import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, LogOut, ShieldCheck, User, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, API } from "@/lib/api";
import { Assistant } from "@/components/Assistant";

let _settingsPromise = null;
function fetchSettingsOnce() {
  if (!_settingsPromise) {
    _settingsPromise = api.get("/settings").then((r) => r?.data || {}).catch(() => ({}));
  }
  return _settingsPromise;
}

function useBranding() {
  const [branding, setBranding] = useState({ logo: null, companyName: "" });
  useEffect(() => {
    let mounted = true;
    fetchSettingsOnce().then((d) => {
      if (!mounted) return;
      const u = d.logo_url;
      setBranding({
        logo: u ? (u.startsWith("/api/") ? `${API.replace(/\/api$/, "")}${u}` : u) : null,
        companyName: d.company_name || "",
      });
      if (d.brand_primary) {
        document.documentElement.style.setProperty("--rcw-amber", d.brand_primary);
        document.documentElement.style.setProperty("--rcw-amber-hover", d.brand_primary);
      }
    });
    return () => { mounted = false; };
  }, []);
  return branding;
}

export function Brand({ size = "md" }) {
  const lg = size === "lg";
  const { logo, companyName } = useBranding();
  return (
    <Link to="/" className="flex items-center gap-3 group" data-testid="brand-home-link">
      {logo ? (
        <img
          src={logo}
          alt={companyName || "Company logo"}
          className={`${lg ? "h-14" : "h-10"} w-auto object-contain`}
        />
      ) : (
        <div className={`${lg ? "h-12 w-12 text-lg" : "h-9 w-9 text-sm"} rounded-lg bg-gray-900 text-white flex items-center justify-center font-bold`}>
          {(companyName || "P").slice(0, 1).toUpperCase()}
        </div>
      )}
      <div className={`leading-tight border-l border-gray-200 pl-3 hidden sm:block`}>
        <div className={`${lg ? "text-base" : "text-sm"} font-bold tracking-tight text-gray-900`}>
          {companyName || "Project Hub"}
        </div>
        <div className={`${lg ? "text-sm" : "text-xs"} text-gray-500`}>
          HTM Project Hub
        </div>
      </div>
    </Link>
  );
}

function UserMenu() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  if (!user) return null;
  const initials = (user.name || user.email).split(/[ @]/)[0].slice(0, 2).toUpperCase();
  const doLogout = () => { logout(); navigate("/login", { replace: true }); };
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className="flex items-center gap-2 rcw-btn-ghost text-sm pl-2 pr-3"
        data-testid="user-menu-button"
      >
        <span className="h-7 w-7 rounded-full bg-gray-900 text-sky-300 text-xs font-bold inline-flex items-center justify-center">
          {initials}
        </span>
        <span className="hidden sm:inline text-gray-900 font-medium max-w-[140px] truncate">
          {user.name || user.email}
        </span>
        {isAdmin && <ShieldCheck className="hidden sm:inline h-3.5 w-3.5 text-sky-500" />}
        <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 w-56 rounded-xl shadow-lg border border-gray-200 bg-white py-1 z-40"
          data-testid="user-menu-dropdown"
        >
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="text-sm font-semibold text-gray-900 truncate">{user.name || "—"}</div>
            <div className="text-xs text-gray-500 truncate">{user.email}</div>
            <div className="mt-1 text-[10px] uppercase tracking-wider text-sky-600 font-bold">
              {isAdmin ? "Administrator" : "Standard user"}
            </div>
          </div>
          <Link to="/account" className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-testid="user-menu-account">
            <User className="h-4 w-4" /> My account
          </Link>
          {isAdmin && (
            <Link to="/admin/users" className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-testid="user-menu-admin-users">
              <Users className="h-4 w-4" /> Users
            </Link>
          )}
          <button
            onClick={doLogout}
            className="w-full text-left flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
            data-testid="user-menu-logout"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export default function Layout({ children, transparent, hideFooter }) {
  const loc = useLocation();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const { companyName } = useBranding();
  useEffect(() => {
    if (!isAdmin) return;
    api.get("/setup/status")
      .then((r) => { if (r?.data?.needs_setup) navigate("/setup"); })
      .catch(() => {});
  }, [isAdmin, navigate]);
  return (
    <div className="min-h-screen flex flex-col" data-testid="app-layout">
      <header
        className={`${
          transparent ? "bg-transparent" : "bg-white border-b border-gray-200"
        } sticky top-0 z-30 backdrop-blur supports-[backdrop-filter]:bg-white/80`}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Brand />
          <nav className="flex items-center gap-1 sm:gap-2">
            {user && !loc.pathname.startsWith("/cvs") && (
              <Link to="/cvs" className="rcw-btn-ghost text-sm hidden sm:inline-flex" data-testid="header-cvs-link">
                CVs
              </Link>
            )}
            {user && !loc.pathname.startsWith("/admin") && (
              <Link to="/admin" className="rcw-btn-ghost text-sm" data-testid="header-projects-link">
                Projects
              </Link>
            )}
            {user && isAdmin && loc.pathname.startsWith("/admin") && (
              <Link to="/admin/settings" className="rcw-btn-ghost text-sm hidden sm:inline-flex" data-testid="header-settings-link">
                Settings
              </Link>
            )}
            <UserMenu />
          </nav>
        </div>
      </header>
      <main className="flex-1">{children}</main>
      <Assistant />
      {!hideFooter && (
        <footer className="border-t border-gray-200 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 text-xs text-gray-500 flex flex-col sm:flex-row gap-2 sm:justify-between">
            <span>© {new Date().getFullYear()} {companyName || "Project Hub"}</span>
            <span>HTM Project Hub</span>
          </div>
        </footer>
      )}
    </div>
  );
}
